import moment from 'moment'
import {
  canTransitionPipelineStatus,
  evaluateCallSurfaceStatusWrite,
  isEarlyCallOutcomeStatus,
  pipelineTransitionBlockedMessage,
} from '@/utils/candidate-status-flow'

export {
  canTransitionPipelineStatus,
  evaluateCallSurfaceStatusWrite,
  pipelineTransitionBlockedMessage,
} from '@/utils/candidate-status-flow'

export function normalizeCandidateStatusKey(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

/** True when no call-outcome write would change pipeline status (not early, not same). */
export function isCallSurfaceLockedStatus(status, nextStatus, context) {
  if (nextStatus) {
    return evaluateCallSurfaceStatusWrite(status, nextStatus, context).reason === 'blocked'
  }
  if (isEarlyCallOutcomeStatus(status) || !normalizeCandidateStatusKey(status)) return false
  return !canTransitionPipelineStatus(status, 'Duplicate', context)
    && !canTransitionPipelineStatus(status, 'Call Back', context)
}

export function canCallSurfaceChangePipelineStatus(currentStatus, nextStatus, context) {
  const verdict = evaluateCallSurfaceStatusWrite(currentStatus, nextStatus, context)
  return verdict.changeStatus
}

export function callSurfaceLockedStatusMessage(currentStatus, nextStatus) {
  if (nextStatus) return pipelineTransitionBlockedMessage(currentStatus, nextStatus)
  const label = String(currentStatus || '').trim() || 'this status'
  return `Cannot change status from ${label} (status flow).`
}

export function resolveJobPipelineStatus(...candidates) {
  for (let i = 0; i < candidates.length; i++) {
    const value = candidates[i]
    if (value && value !== '—') return value
  }
  return ''
}

export function buildCallNotesOnlyPayload({
  userData,
  comment = '',
  callNotes = '',
}) {
  const username = `${(userData && userData.first_name) || ''} ${(userData && userData.last_name) || ''}`.trim()
  const user = `[{"title" : "${username}", "emp_id": "${(userData && userData.emp_id) || ''}"}]`
  return {
    extra_details: { callNotes: callNotes || comment || '' },
    updatedBy: user,
  }
}

export const POST_CALL_STATUS_OPTIONS = [
  { value: '', text: '— No status change —' },
  { value: 'Call Back', text: 'Call Back' },
  { value: 'Not Answered', text: 'Not Answered' },
  { value: 'Not Reachable', text: 'Not Reachable' },
  { value: 'Wrong Number', text: 'Wrong Number' },
  { value: 'Not Interested', text: 'Not Interested' },
  { value: 'Not Relevant', text: 'Not Relevant' },
  { value: 'Duplicate', text: 'Duplicate' },
  { value: 'Interested', text: 'Interested' },
]

export const NOT_INTERESTED_REASON_OPTIONS = [
  { value: null, text: 'Select not interested reason…', disabled: true },
  { value: 'Not Looking for Change', text: 'Not Looking for Change' },
  { value: 'Not ok with the Client/Company', text: 'Not ok with the Client/Company' },
  { value: 'Not ok with Location', text: 'Not ok with Location' },
  { value: 'Not ok with Shift', text: 'Not ok with Shift' },
  { value: 'Not ok with Compensation', text: 'Not ok with Compensation' },
  { value: 'Not ok with Designation/Role', text: 'Not ok with Designation/Role' },
  { value: 'Recently Changed', text: 'Recently Changed' },
  { value: 'Already Interviewed/Duplicate', text: 'Already Interviewed/Duplicate' },
  { value: 'Already working with Organization', text: 'Already working with Organization' },
  { value: 'Re-Hire', text: 'Re-Hire' },
  { value: 'Ex-Employee', text: 'Ex-Employee' },
  { value: 'Other', text: 'Other' },
]

export const DUPLICATE_REASON_OPTIONS = [
  { value: null, text: 'Select duplicate reason…', disabled: true },
  { value: 'Other Vendor Source Lock', text: 'Other Vendor Source Lock' },
  { value: 'Profile in Cooling Period', text: 'Profile in Cooling Period' },
]

export const COOLING_TIMELINE_OPTIONS = [
  { value: null, text: 'Select cooling period…', disabled: true },
  { value: 0, text: '0' },
  { value: 30, text: '1 Month' },
  { value: 60, text: '2 Months' },
  { value: 90, text: '3 Months' },
  { value: 120, text: '4 Months' },
  { value: 150, text: '5 Months' },
  { value: 180, text: '6 Months' },
]

export function computeDuplicateDateFromCooling(coolingTimeline) {
  if (coolingTimeline == null) return null
  return moment().add(coolingTimeline, 'days').format('D MMM YYYY')
}

/**
 * Build candidate status payload for post-call outcome (Dialer / My Calls).
 */
export function buildCallOutcomeStatusPayload({
  postCallStatus,
  userData,
  jobRecord = null,
  notInterestedReason = null,
  duplicateReason = null,
  coolingTimeline = null,
  comment = '',
  callNotes = '',
  canReadTalentAcquisition = false,
  recruiterMap = {},
}) {
  const username = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
  const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
  const mgrId = userData.mgr_id
  const mgrName = (recruiterMap[String(mgrId)] || username).toUpperCase()
  const pMgrId = (jobRecord && jobRecord.p_mgr_id) || ''

  if (postCallStatus === 'Duplicate') {
    return {
      candidateStatus: 'Duplicate',
      updatedBy: user,
      reason: `${duplicateReason}| Cooling Time: ${coolingTimeline} days`,
      duplicate_date: computeDuplicateDateFromCooling(coolingTimeline),
      recruiter: userData.emp_id,
      mgr_id: mgrId,
      p_mgr_id: pMgrId,
      'Manager Name': canReadTalentAcquisition ? mgrName : username.toUpperCase(),
      'Employee Name': username.toUpperCase(),
      extra_details: { callNotes: callNotes || '' },
    }
  }

  if (postCallStatus === 'Not Interested') {
    return {
      candidateStatus: 'Not Interested',
      updatedBy: user,
      reason: notInterestedReason,
      comment: comment || '',
      recruiter: userData.emp_id,
      mgr_id: mgrId,
      p_mgr_id: pMgrId,
      'Manager Name': canReadTalentAcquisition ? mgrName : username.toUpperCase(),
      'Employee Name': username.toUpperCase(),
    }
  }

  // Match OutboundCallModal saveAndClose default branch (all statuses except Duplicate / Not Interested).
  return {
    candidateStatus: postCallStatus,
    extra_details: { callNotes: callNotes || comment || '' },
    // Same JSON-stringified-array shape as the Duplicate/Not Interested
    // branches above (NOT a bare emp_id string) — `CandidatesView.vue`'s
    // "Updated by" field does `JSON.parse(candidateData.updated_by)[0].title`
    // unconditionally (no try/catch or `isValidJson()` guard, unlike its
    // sibling `created_by` field), so a bare emp_id string here would throw
    // a JSON parse error and break that display for any candidate whose
    // most recent status update came through this branch.
    updatedBy: user,
  }
}

export function validateCallOutcomeForm({
  connected,
  postCallStatus,
  notInterestedReason,
  duplicateReason,
  coolingTimeline,
  comment,
}) {
  if (connected === false) return { valid: true }

  if (!postCallStatus) {
    return { valid: false, message: 'Please select a call outcome before saving.' }
  }

  if (postCallStatus === 'Not Interested' && !notInterestedReason) {
    return { valid: false, message: 'Please select a not interested reason.' }
  }

  if (postCallStatus === 'Duplicate') {
    if (!duplicateReason) return { valid: false, message: 'Please select a duplicate reason.' }
    if (coolingTimeline == null) return { valid: false, message: 'Please select a cooling period.' }
  }

  if (postCallStatus === 'Not Relevant' && !String(comment || '').trim()) {
    return { valid: false, message: 'Please add a comment explaining why the candidate is not relevant.' }
  }

  return { valid: true }
}
